// The official forms of Ord. MDLPA nr. 975/2026, drawn from the same description the server uses for the PDFs. Shared by the DMS
// (desk intake, the act's editor, read-only views) and the citizen portal. Plain inputs and own classes (ff-*), so it fits both apps.
import React from 'react';
import { LocationPicker, isLocation, locationText } from '../maps/components';

export interface FField { k: string; l: string; t: string; w?: number; req?: boolean; hint?: string; who?: 'A' | 'U'; o?: string[]; cols?: { k: string; l: string }[]; rows?: number; signer?: string }
export interface FSection { no?: string; title?: string; fields: FField[]; who?: string }
export interface FSpec { code: string; version: string; annex: number; kind: string; title: string; heading?: string; request?: string; legal?: string; procedure?: string; portal?: boolean; sections: FSection[]; institution?: Record<string, string> }
export type FMode = 'applicant' | 'authority' | 'view';

const fmtDate = (s?: string | null) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || ''); return m ? `${m[3]}.${m[2]}.${m[1]}` : s || ''; };
export function fieldText(f: FField, v: any): string {
  if (v == null || v === '') return '';
  if (f.t === 'loc') return isLocation(v) ? `${locationText(v)} (${v.lat.toFixed(5)}, ${v.lon.toFixed(5)})` : '';
  if (f.t === 'd') return fmtDate(String(v));
  if (f.t === 'c') return v ? 'Da' : '';
  if (Array.isArray(v)) return f.t === 'tbl' ? `${v.length} rânduri` : v.join('; ');
  return String(v);
}
/** The applicant fills what is not the authority's; the authority fills its zone — and every field of an act (the acts are entirely its own). */
const editable = (f: FField, mode: FMode, act?: boolean) => f.t === 'sig' || f.t === 'p' ? false : mode === 'applicant' ? f.who !== 'U' : mode === 'authority' ? act || f.who === 'U' : false;
export const isEmpty = (f: FField, v: any) => v == null || v === '' || (Array.isArray(v) && v.length === 0) || (f.t === 'loc' && !isLocation(v)) || (f.t === 'c' && !v);

/** The required fields of the given side that are still empty. */
export function missing(spec: FSpec, values: Record<string, any>, mode: FMode): Record<string, string> {
  const e: Record<string, string> = {};
  for (const s of spec.sections) for (const f of s.fields) if (f.req && f.t !== 'sig' && f.t !== 'p' && editable(f, mode, spec.kind === 'ACT') && isEmpty(f, values[f.k])) e[f.k] = f.t === 'loc' ? 'Alegeți locul pe hartă' : 'Câmp obligatoriu';
  return e;
}

/** A point picked on the map fills the empty address fields of the same block (județ, localitate, stradă, număr, cod poștal). */
function withAddress(spec: FSpec, values: Record<string, any>, p: string, loc: any): Record<string, any> {
  const keys = new Set(spec.sections.flatMap(s => s.fields.map(f => f.k)));
  const fill: Record<string, any> = { judet: loc.county, localitate: loc.uat || loc.locality, strada: loc.street, nr: loc.housenumber, cod: loc.postcode };
  const out = { ...values };
  for (const [k, v] of Object.entries(fill)) if (v && keys.has(p + k) && (out[p + k] == null || out[p + k] === '')) out[p + k] = String(v).replace(/^(Județul|Judetul)\s+/i, '');
  return out;
}

export function FormView({ spec, values, onChange, mode, errors, hideOther, signNote }: {
  spec: FSpec; values: Record<string, any>; onChange?: (v: Record<string, any>) => void; mode: FMode; errors?: Record<string, string>;
  /** hide the sections the other side fills (the citizen does not see the authority's zone) */ hideOther?: boolean; signNote?: string;
}) {
  const set = (k: string, v: any) => onChange?.(k.endsWith('_loc') && isLocation(v) ? withAddress(spec, { ...values, [k]: v }, k.slice(0, -3), v) : { ...values, [k]: v });
  return (
    <div className="ff">
      {spec.sections.map((s, i) => {
        const fields = s.fields.filter(f => !(hideOther && mode === 'applicant' && f.who === 'U'));
        if (!fields.length) return null;
        return (
          <section key={i} className={'ff-sec' + (s.who === 'U' && mode === 'applicant' ? ' other' : '')}>
            {(s.no || s.title) && <h4 className="ff-sec-title">{s.no ? <span className="ff-no">{s.no}.</span> : null}{s.title}</h4>}
            <div className="ff-grid">
              {fields.map(f => <FieldBox key={f.k} f={f} value={values[f.k]} set={v => set(f.k, v)} edit={editable(f, mode, spec.kind === 'ACT')} error={errors?.[f.k]} signNote={signNote} />)}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function FieldBox({ f, value, set, edit, error, signNote }: { f: FField; value: any; set: (v: any) => void; edit: boolean; error?: string; signNote?: string }) {
  const span = ['p', 'm', 'r', 'tbl', 'loc', 'ta'].includes(f.t) ? 4 : Math.max(1, Math.min(4, f.w || 2));
  const cls = 'ff-f s' + span + (error ? ' invalid' : '') + (edit ? '' : ' ro');
  if (f.t === 'p') return <p className="ff-p s4">{f.l}</p>;
  if (f.t === 'sig') return <div className={'ff-sig s' + Math.max(1, Math.min(4, f.w || 2))}><span className="ff-l">{f.l}</span><span className="ff-sig-note">{signNote || (f.signer ? 'semnătura electronică se aplică în modul' : 'semnătura')}</span></div>;
  if (f.t === 'c') return (
    <label className={cls + ' ff-check'}>
      <input type="checkbox" checked={!!value} disabled={!edit} onChange={e => set(e.target.checked)} /><span>{f.l}{f.req && edit && <b className="ff-req"> *</b>}</span>
      {error && <small className="ff-err">{error}</small>}
    </label>
  );
  return (
    <div className={cls}>
      <label className="ff-l">{f.l}{f.req && edit && <b className="ff-req"> *</b>}</label>
      {edit ? <Input f={f} value={value} set={set} /> : <ReadOnly f={f} value={value} />}
      {error ? <small className="ff-err">{error}</small> : f.hint && edit ? <small className="ff-hint">{f.hint}</small> : null}
    </div>
  );
}

function Input({ f, value, set }: { f: FField; value: any; set: (v: any) => void }) {
  switch (f.t) {
    case 'ta': return <textarea className="ff-in" rows={3} value={value ?? ''} onChange={e => set(e.target.value)} />;
    case 'n': return <input className="ff-in" type="number" step="any" value={value ?? ''} onChange={e => set(e.target.value === '' ? null : Number(e.target.value))} />;
    case 'd': return <input className="ff-in" type="date" value={value ? String(value).slice(0, 10) : ''} onChange={e => set(e.target.value || null)} />;
    case 'r': return (
      <div className="ff-opts">
        {(f.o || []).map(o => <label key={o} className={'ff-opt' + (value === o ? ' on' : '')}><input type="radio" checked={value === o} onChange={() => set(o)} /><span>{o}</span></label>)}
        {value && !f.req && <button type="button" className="ff-clear" onClick={() => set(null)}>șterge alegerea</button>}
      </div>
    );
    case 'm': {
      const list: string[] = Array.isArray(value) ? value : [];
      return (
        <div className="ff-opts">
          {(f.o || []).map(o => <label key={o} className={'ff-opt' + (list.includes(o) ? ' on' : '')}><input type="checkbox" checked={list.includes(o)} onChange={e => set(e.target.checked ? [...list, o] : list.filter(x => x !== o))} /><span>{o}</span></label>)}
        </div>
      );
    }
    case 'loc': return <LocationPicker value={isLocation(value) ? value : null} onChange={set} required={f.req} height={280} />;
    case 'tbl': return <Table f={f} value={value} set={set} />;
    default: return <input className="ff-in" value={value ?? ''} onChange={e => set(e.target.value)} />;
  }
}

function ReadOnly({ f, value }: { f: FField; value: any }) {
  if (f.t === 'm' || f.t === 'r') {
    const on: string[] = Array.isArray(value) ? value : value ? [value] : [];
    if (!on.length) return <span className="ff-v muted">—</span>;
    return <ul className="ff-vlist">{on.map(o => <li key={o}>☒ {o}</li>)}</ul>;
  }
  if (f.t === 'tbl') {
    const rows: Record<string, any>[] = Array.isArray(value) ? value : [];
    if (!rows.length) return <span className="ff-v muted">—</span>;
    return <table className="ff-table"><thead><tr>{(f.cols || []).map(c => <th key={c.k}>{c.l}</th>)}</tr></thead><tbody>{rows.map((r, i) => <tr key={i}>{(f.cols || []).map(c => <td key={c.k}>{r[c.k] ?? ''}</td>)}</tr>)}</tbody></table>;
  }
  const t = fieldText(f, value);
  return <span className={'ff-v' + (t ? '' : ' muted')}>{t || '—'}</span>;
}

function Table({ f, value, set }: { f: FField; value: any; set: (v: any) => void }) {
  const rows: Record<string, any>[] = Array.isArray(value) ? value : [];
  const cols = f.cols || [];
  const upd = (i: number, k: string, v: string) => set(rows.map((r, j) => j === i ? { ...r, [k]: v } : r));
  return (
    <div className="ff-tbl">
      <table className="ff-table">
        <thead><tr>{cols.map(c => <th key={c.k}>{c.l}</th>)}<th /></tr></thead>
        <tbody>
          {rows.map((r, i) => <tr key={i}>{cols.map(c => <td key={c.k}><input className="ff-in" value={r[c.k] ?? ''} onChange={e => upd(i, c.k, e.target.value)} /></td>)}
            <td><button type="button" className="ff-clear" onClick={() => set(rows.filter((_, j) => j !== i))} title="Elimină rândul">✕</button></td></tr>)}
        </tbody>
      </table>
      <button type="button" className="ff-add" onClick={() => set([...rows, {}])}>+ Adaugă un rând</button>
    </div>
  );
}
